import { AbilityBuilder, InferSubjects, PureAbility, createMongoAbility } from '@casl/ability';
import { AbsolvedCheck, USER_ROLE, User } from '../..';
import { ORGANIZATION_ROLE, OrganizationMember } from '../organization';

export type AbsolvedCheckActions = 'manage' | 'read' | 'delete';
export type AbsolvedCheckSubjects = InferSubjects<AbsolvedCheck | 'AbsolvedCheck'>;

export function defineAbsolvedCheckAbilitiesFor({
  user,
  org,
}: {
  user: User;
  org?: {
    reqOrgMember: OrganizationMember;
    checkUserOrgMember: OrganizationMember | null;
    /** Prefer this when present — allows org access after the author left/was deleted */
    checkOrganizationId?: number | null;
  } | null;
}) {
  const { can, build } = new AbilityBuilder<PureAbility<[AbsolvedCheckActions, AbsolvedCheckSubjects]>>(
    createMongoAbility
  );

  if (user.role === USER_ROLE.ADMIN) {
    // Admins can do anything
    can('manage', 'AbsolvedCheck');
  } else {
    if (org) {
      /**
       * We are in an organization context
       */
      const sameOrgViaCheck =
        org.checkOrganizationId != null && org.checkOrganizationId === org.reqOrgMember.organizationId;
      const sameOrgViaMembership =
        org.reqOrgMember.organizationId === org.checkUserOrgMember?.organizationId;
      if (!sameOrgViaCheck && !sameOrgViaMembership) {
        // The check does not belong to the requester's organization
        return build();
      }
      if (org.reqOrgMember?.role === ORGANIZATION_ROLE.MEMBER) {
        // Members can only read their own checks
        can('read', 'AbsolvedCheck', { userId: user.id });
      }
      if (org.reqOrgMember?.role === ORGANIZATION_ROLE.MANAGER) {
        can('read', 'AbsolvedCheck');
      }
      if (org.reqOrgMember?.role === ORGANIZATION_ROLE.LOCAL_ADMIN) {
        can(['read', 'delete'], 'AbsolvedCheck');
      }
    } else {
      /**
       * We are in a standalone context
       */
      can('manage', 'AbsolvedCheck', { userId: user.id });
    }
  }

  return build();
}
